import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { z } from "zod";

import { apiRequest } from "@/lib/api";
import { UserReadSchema, type UserRead } from "@/lib/schemas/users";
import {
  ActivationCompleteRequestSchema,
  ActivationStatusResponseSchema,
  LoginRequestSchema,
  LoginResponseSchema,
  MfaEnrollRequestSchema,
  MfaEnrollResponseSchema,
  MfaVerifyRequestSchema,
  MfaVerifyResponseSchema,
  PasswordChangeRequestSchema,
  PasswordForgotRequestSchema,
  PasswordResetRequestSchema,
  RecoveryCodesGenerateResponseSchema,
  SessionListItemSchema,
  StepUpRequestSchema,
  StepUpResponseSchema,
  type ActivationCompleteRequest,
  type ActivationStatusResponse,
  type LoginRequest,
  type LoginResponse,
  type MfaEnrollRequest,
  type MfaEnrollResponse,
  type MfaVerifyRequest,
  type MfaVerifyResponse,
  type PasswordChangeRequest,
  type PasswordForgotRequest,
  type PasswordResetRequest,
  type RecoveryCodesGenerateResponse,
  type SessionListItem,
  type StepUpRequest,
  type StepUpResponse,
} from "@/lib/schemas/auth";

/* ────────────────────────────────────────────── Local login (Phase 10A) */

function _uuidv4(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback (jsdom test env): RFC 4122 v4 best-effort
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function useLocalLogin(): UseMutationResult<
  LoginResponse,
  Error,
  LoginRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: LoginRequest) => {
      const validated = LoginRequestSchema.parse(input);
      const raw = await apiRequest<unknown>("/auth/login", {
        method: "POST",
        body: validated,
        idempotencyKey: _uuidv4(),
      });
      return LoginResponseSchema.parse(raw);
    },
    onSuccess: () => {
      // Invalidate any cached auth state so /users/me etc. refetch fresh.
      void queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });
}

/* ────────────────────────────────────────────── Current user (/users/me) */

export function useCurrentUser(): UseQueryResult<UserRead> {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const raw = await apiRequest<unknown>("/users/me");
      return UserReadSchema.parse(raw);
    },
    staleTime: 60_000,
  });
}

/* ────────────────────────────────────────────── MFA */

export function useMfaEnroll(): UseMutationResult<
  MfaEnrollResponse,
  Error,
  MfaEnrollRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: MfaEnrollRequest) => {
      const validated = MfaEnrollRequestSchema.parse(input);
      const raw = await apiRequest<unknown>("/auth/mfa/enroll", {
        method: "POST",
        body: validated,
      });
      return MfaEnrollResponseSchema.parse(raw);
    },
    onSuccess: () => {
      // Enroll flips app_users.mfa_enrolled → invalidate ALL auth state (incl.
      // ["auth","me"]) so the forced-MFA banner clears without a manual refresh.
      void queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });
}

export function useMfaVerify(): UseMutationResult<
  MfaVerifyResponse,
  Error,
  MfaVerifyRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: MfaVerifyRequest) => {
      const validated = MfaVerifyRequestSchema.parse(input);
      const raw = await apiRequest<unknown>("/auth/mfa/verify", {
        method: "POST",
        body: validated,
      });
      return MfaVerifyResponseSchema.parse(raw);
    },
    onSuccess: () => {
      // Verify refreshes mfa_assured_until + (post-enroll) mfa_enrolled →
      // invalidate ALL auth state (incl. ["auth","me"]) so the banner clears.
      void queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });
}

/* ────────────────────────────────────────────── Step-up (POST /auth/step-up) */

/**
 * Obtain an X-Step-Up-Token for an `action_class` by re-verifying TOTP.
 * The returned `step_up_token` is passed via `stepUpToken` to the gated call
 * (e.g. POST /users/{id}/role). Not cached — fresh per sensitive action.
 */
export function useStepUp(): UseMutationResult<StepUpResponse, Error, StepUpRequest> {
  return useMutation({
    mutationFn: async (input: StepUpRequest) => {
      const validated = StepUpRequestSchema.parse(input);
      const raw = await apiRequest<unknown>("/auth/step-up", {
        method: "POST",
        body: validated,
      });
      return StepUpResponseSchema.parse(raw);
    },
  });
}

/* ────────────────────────────────────────────── MFA disable (Phase 10B) */

export function useMfaDisable(): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiRequest<unknown>("/auth/mfa/totp", { method: "DELETE" });
    },
    onSuccess: () => {
      // Refresh /users/me (mfa_enrolled flipped) + sessions list
      void queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });
}

/* ────────────────────────────────────────────── Recovery codes */

export function useRecoveryCodesGenerate(): UseMutationResult<
  RecoveryCodesGenerateResponse,
  Error,
  void
> {
  return useMutation({
    mutationFn: async () => {
      const raw = await apiRequest<unknown>("/auth/mfa/recovery/generate", {
        method: "POST",
      });
      return RecoveryCodesGenerateResponseSchema.parse(raw);
    },
  });
}

/* ────────────────────────────────────────────── Sessions list / revoke */

const SessionsListResponseSchema = z.array(SessionListItemSchema);

export function useSessionsList(): UseQueryResult<SessionListItem[]> {
  return useQuery({
    queryKey: ["auth", "sessions", "list"],
    queryFn: async () => {
      const raw = await apiRequest<unknown>("/auth/sessions");
      return SessionsListResponseSchema.parse(raw);
    },
    staleTime: 30_000,
  });
}

export function useSessionRevoke(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await apiRequest<unknown>(`/auth/sessions/${sessionId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] });
    },
  });
}

/* ────────────────────────────────────────────── Password change */

export function usePasswordChange(
  userId: string | undefined,
): UseMutationResult<void, Error, PasswordChangeRequest> {
  return useMutation({
    mutationFn: async (input: PasswordChangeRequest) => {
      if (!userId) throw new Error("userId is required");
      const validated = PasswordChangeRequestSchema.parse(input);
      // Backend accetta `current_password` + `new_password`; il
      // confirm_password e' client-side only.
      const { confirm_password: _ignored, ...payload } = validated;
      void _ignored;
      await apiRequest<unknown>(`/users/${userId}/password`, {
        method: "POST",
        body: payload,
      });
    },
  });
}

/* ────────────────────────────────────────────── Activation (Phase 10G) */

/**
 * GET /auth/activation/{token} — pre-form status check. Non consuma il token.
 * Ritorna validity + expiry per UI rendering del form (vs "token invalid" page).
 */
export function useActivationStatus(
  token: string | undefined,
): UseQueryResult<ActivationStatusResponse> {
  return useQuery({
    queryKey: ["auth", "activation", "status", token],
    queryFn: async () => {
      if (!token) throw new Error("token is required");
      const raw = await apiRequest<unknown>(`/auth/activation/${encodeURIComponent(token)}`);
      return ActivationStatusResponseSchema.parse(raw);
    },
    enabled: Boolean(token),
    // No staleTime: ogni mount fa una re-check. Il token e' opaque + single-use.
    staleTime: 0,
    retry: false,
  });
}

/**
 * POST /auth/activation/{token} — completa activation con password fresca.
 * Single-use: il token viene marcato consumato server-side anche su
 * PasswordPolicy violation (vedi BE activation_complete: mark_used DOPO
 * password set per permettere retry su 422 senza ri-richiedere token).
 */
export function useActivationComplete(
  token: string | undefined,
): UseMutationResult<void, Error, ActivationCompleteRequest> {
  return useMutation({
    mutationFn: async (input: ActivationCompleteRequest) => {
      if (!token) throw new Error("token is required");
      const validated = ActivationCompleteRequestSchema.parse(input);
      await apiRequest<unknown>(`/auth/activation/${encodeURIComponent(token)}`, {
        method: "POST",
        body: validated,
      });
    },
  });
}

/** POST /auth/password/forgot — sempre 202 (anti-enumeration), nessun body. */
export function useForgotPassword(): UseMutationResult<void, Error, PasswordForgotRequest> {
  return useMutation({
    mutationFn: async (input: PasswordForgotRequest) => {
      const validated = PasswordForgotRequestSchema.parse(input);
      await apiRequest<unknown>("/auth/password/forgot", { method: "POST", body: validated });
    },
  });
}

/** POST /auth/password/reset — token (URL) + nuova password + OTP (se MFA enrollata). 204. */
export function useResetPassword(): UseMutationResult<void, Error, PasswordResetRequest> {
  return useMutation({
    mutationFn: async (input: PasswordResetRequest) => {
      const validated = PasswordResetRequestSchema.parse(input);
      await apiRequest<unknown>("/auth/password/reset", { method: "POST", body: validated });
    },
  });
}
